import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../i18n/bookingCopy';
import {
  TutorBookingChangeProposal,
  TutorBookingChangeStatus,
} from '../models/TutorBookingChange';
import {
  formatProposalDateTime,
  formatTutorBookingScheduleLabels,
  getProposalNewEndIso,
  getProposalOldEndIso,
  getTutorBookingPreviousTimeLabel,
  getTutorChangeProposalAutoAcceptAlert,
  getTutorChangeProposalReason,
  isTutorChangeProposalActionable,
  isTutorChangeProposalTerminal,
  proposalTimeChanged,
} from '../utils/tutorBookingChangeProposal';
import TutorBookingChangeTimeComparison from './TutorBookingChangeTimeComparison';
import TutorBookingChangeProposalScheduleChip from './tutor/TutorBookingChangeProposalScheduleChip';
import TutorBookingScheduleBlock, {
  TutorBookingScheduleBlockProps,
} from './tutor/TutorBookingScheduleBlock';

export interface TutorBookingChangeProposalSectionProps {
  proposal: TutorBookingChangeProposal;
  bookingStartTime: string;
  bookingEndTime: string;
  timezone?: string | null;
  processing: boolean;
  actionError?: string | null;
  /** Tighter layout for booking detail pages */
  compact?: boolean;
  /** When schedule is shown elsewhere (e.g. details page date/time block). */
  hideSchedule?: boolean;
  /**
   * Actions only (no card / copy): for embedding under the details schedule row.
   * Dialogs still render when accept/decline is used.
   */
  embedded?: boolean;
  /** When embedded, render actions inside this schedule block instead of below it. */
  scheduleBlockProps?: Omit<TutorBookingScheduleBlockProps, 'actions'>;
  onAccept: () => Promise<void>;
  onDecline: (reason: string) => Promise<void>;
}

export default function TutorBookingChangeProposalSection({
  proposal,
  bookingStartTime,
  bookingEndTime,
  timezone,
  processing,
  actionError,
  compact = false,
  hideSchedule = false,
  embedded = false,
  scheduleBlockProps,
  onAccept,
  onDecline,
}: TutorBookingChangeProposalSectionProps) {
  const { t, i18n } = useTranslation();
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const proposalReason = getTutorChangeProposalReason(proposal);
  const timeChanged = proposalTimeChanged(proposal);
  const actionable = isTutorChangeProposalActionable(proposal.status);
  const terminal = isTutorChangeProposalTerminal(proposal.status);

  const oldEndIso = getProposalOldEndIso(proposal, bookingStartTime, bookingEndTime);
  const newEndIso = getProposalNewEndIso(proposal, bookingStartTime, bookingEndTime);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    }
    return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
  };

  const proposalScheduleOptions = { source: 'proposal' as const, timezone };
  const proposedSchedule = formatTutorBookingScheduleLabels(
    proposal.newStartTime,
    newEndIso,
    i18n.language,
    formatDuration,
    proposalScheduleOptions,
  );
  const previousSchedule = formatTutorBookingScheduleLabels(
    proposal.oldStartTime,
    oldEndIso,
    i18n.language,
    formatDuration,
    proposalScheduleOptions,
  );
  const showProposedAsPrimary =
    actionable || proposal.status === TutorBookingChangeStatus.ACCEPTED;
  const scheduleForBlock = showProposedAsPrimary ? proposedSchedule : previousSchedule;
  const previousVisibleInline = actionable && timeChanged;
  const previousTimeLabel = previousVisibleInline
    ? getTutorBookingPreviousTimeLabel(previousSchedule, proposedSchedule)
    : undefined;

  const statusChip = (
    <TutorBookingChangeProposalScheduleChip
      proposal={proposal}
      bookingStartTime={bookingStartTime}
      bookingEndTime={bookingEndTime}
      locale={i18n.language}
      audience="user"
      timezone={timezone}
      showPreviousTimeInTooltip={!previousVisibleInline}
    />
  );

  const handleConfirmAccept = async () => {
    await onAccept();
    setAcceptDialogOpen(false);
  };

  const handleConfirmDecline = async () => {
    const trimmed = declineReason.trim();
    if (!trimmed) return;
    await onDecline(trimmed);
    setDeclineDialogOpen(false);
    setDeclineReason('');
  };

  const autoAcceptAlert = getTutorChangeProposalAutoAcceptAlert(
    proposal.status,
    proposal.autoAcceptTutorBooking,
    t,
  );

  const terminalMessage =
    proposal.status === TutorBookingChangeStatus.ACCEPTED
      ? t(
          'userTutorBookings.changeProposal.terminalAccepted',
          'You accepted the proposed lesson time. The booking reflects the updated schedule.',
        )
      : proposal.status === TutorBookingChangeStatus.DECLINED
        ? t(
            'userTutorBookings.changeProposal.terminalDeclined',
            'You declined this change proposal. The original lesson time stays in place.',
          )
        : t(
            'userTutorBookings.changeProposal.terminalCancelled',
            'This change proposal was cancelled and no longer requires a response.',
          );

  const blockSpacing = 2;
  const sectionGap = compact ? 2 : blockSpacing;

  const dialogs = (
    <>
      <Dialog open={acceptDialogOpen} onClose={() => !processing && setAcceptDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('userTutorBookings.changeProposal.acceptTitle', 'Accept new lesson time?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {proposal.autoAcceptTutorBooking
              ? t(
                  'userTutorBookings.changeProposal.acceptMessageAutoConfirm',
                  'Your booking will move to the proposed time and be confirmed automatically.',
                )
              : t(
                  'userTutorBookings.changeProposal.acceptMessage',
                  'Your booking will move to the proposed time. This cannot be undone from here.',
                )}
          </Typography>
          {timeChanged && (
            <TutorBookingChangeTimeComparison
              oldStartIso={proposal.oldStartTime}
              oldEndIso={oldEndIso}
              newStartIso={proposal.newStartTime}
              newEndIso={newEndIso}
              locale={i18n.language}
              timezone={timezone}
              compact
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAcceptDialogOpen(false)} disabled={processing}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="success" onClick={handleConfirmAccept} disabled={processing}>
            {t('bookings.changeProposal.confirmAccept', 'Accept')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={declineDialogOpen} onClose={() => !processing && setDeclineDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('userTutorBookings.changeProposal.declineTitle', 'Keep original lesson time')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t(
              'userTutorBookings.changeProposal.declineMessage',
              'The tutor will see your reason. The lesson stays at the current scheduled time.',
            )}
          </Typography>
          <TextField
            fullWidth
            required
            margin="dense"
            label={t('userTutorBookings.changeProposal.declineReason', 'Why does the new time not work?')}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            multiline
            rows={3}
            placeholder={t(
              'userTutorBookings.changeProposal.declineReasonPlaceholder',
              'e.g. I have another commitment that day…',
            )}
            error={declineDialogOpen && declineReason.trim() === ''}
            helperText={
              declineReason.trim() === ''
                ? t('userTutorBookings.changeProposal.declineReasonRequired', 'A reason is required to decline.')
                : undefined
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeclineDialogOpen(false)} disabled={processing}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDecline}
            disabled={processing || declineReason.trim() === ''}
          >
            {t('userTutorBookings.changeProposal.confirmDecline', 'Decline change')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const actionButtons =
    actionable ? (
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          variant="contained"
          color="success"
          size="small"
          disabled={processing}
          onClick={() => setAcceptDialogOpen(true)}
          startIcon={processing ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{
            flex: 1,
            minWidth: 0,
            py: 0.85,
            px: 1.25,
            fontSize: '0.8125rem',
            fontWeight: 700,
            lineHeight: 1.25,
            minHeight: 40,
            borderRadius: 1,
          }}
        >
          {t('userTutorBookings.changeProposal.acceptShort', 'Accept')}
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={processing}
          onClick={() => {
            setDeclineReason('');
            setDeclineDialogOpen(true);
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            py: 0.85,
            px: 1.25,
            fontSize: '0.8125rem',
            fontWeight: 700,
            lineHeight: 1.25,
            minHeight: 40,
            borderRadius: 1,
            borderColor: 'error.main',
            color: 'error.main',
          }}
        >
          {t('userTutorBookings.changeProposal.keepOriginalShort', 'Keep original')}
        </Button>
      </Stack>
    ) : null;

  if (embedded) {
    const inlineActions =
      actionable || actionError ? (
        <Stack spacing={0.75}>
          {actionError ? (
            <Alert severity="error" sx={{ py: 0.25 }}>
              {actionError}
            </Alert>
          ) : null}
          {actionButtons}
        </Stack>
      ) : null;

    if (scheduleBlockProps) {
      return (
        <>
          <TutorBookingScheduleBlock {...scheduleBlockProps} actions={inlineActions} />
          {dialogs}
        </>
      );
    }

    if (!inlineActions) {
      return dialogs;
    }

    return (
      <>
        {inlineActions}
        {dialogs}
      </>
    );
  }

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          p: 2.5,
          mt: compact ? 0 : 2.5,
          borderColor: actionable && !compact ? 'warning.main' : 'divider',
          borderWidth: actionable && !compact ? 2 : 1,
          bgcolor: 'background.paper',
        }}
      >
        {compact ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: sectionGap }}>
            {t('userTutorBookings.changeProposal.sectionTitle', 'Schedule change')}
          </Typography>
        ) : (
          <Typography variant="h6" fontWeight={600}>
            {t('userTutorBookings.changeProposal.sectionTitle', 'Schedule change')}
          </Typography>
        )}

        {actionable && !compact && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {t(
              'userTutorBookings.changeProposal.detailIntro',
              'Your tutor suggested a new lesson time. Compare the times below, then accept or decline.',
            )}
          </Typography>
        )}

        {actionable && compact && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: sectionGap }}>
            {t(
              'userTutorBookings.changeProposal.compactHint',
              'Declining keeps the original time (reason required).',
            )}
          </Typography>
        )}

        {autoAcceptAlert && (
          <Alert severity={autoAcceptAlert.severity} sx={{ mt: blockSpacing, py: compact ? 0.25 : 0.5 }}>
            {autoAcceptAlert.message}
          </Alert>
        )}

        {actionable && !compact && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
            {t(
              'userTutorBookings.changeProposal.actionDeadlineHint',
              'If you decline, the lesson stays at the original time. A reason is required when declining.',
            )}
          </Alert>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mt: blockSpacing, py: compact ? 0.25 : 0.5 }}>
            {actionError}
          </Alert>
        )}

        {terminal &&
          (compact ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: timeChanged ? sectionGap : 0 }}>
                {terminalMessage}
              </Typography>
            </>
          ) : (
            <Alert severity="info" sx={{ mt: blockSpacing }}>
              {terminalMessage}
            </Alert>
          ))}

        {proposalReason &&
          (compact ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: sectionGap }}>
              {t('userTutorBookings.changeProposal.compactReason', 'Note: {{reason}}', {
                reason: proposalReason,
              })}
            </Typography>
          ) : (
            <Box sx={{ mt: blockSpacing }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('userTutorBookings.changeProposal.messageFromTutor', 'Message from tutor')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25, fontStyle: 'italic' }}>
                “{proposalReason}”
              </Typography>
            </Box>
          ))}

        {timeChanged && !hideSchedule && (
          <Box sx={{ mb: compact && (actionable || proposal.repliedAt) ? sectionGap : 0 }}>
            <TutorBookingScheduleBlock
              schedule={scheduleForBlock}
              emphasize={actionable}
              previousTimeLabel={previousTimeLabel}
              statusChip={statusChip}
            />
          </Box>
        )}

        {!timeChanged && !hideSchedule && (
          <Box sx={{ mb: compact && (actionable || proposal.repliedAt) ? sectionGap : 0 }}>
            <Stack direction="row" justifyContent="flex-start">
              {statusChip}
            </Stack>
          </Box>
        )}

        {actionable && (
          <Stack
            direction={compact ? 'column' : { xs: 'column', sm: 'row' }}
            spacing={compact ? 1 : 1.5}
            sx={{ mt: blockSpacing }}
          >
            <Button
              variant="contained"
              color="success"
              size={compact ? 'small' : 'large'}
              disabled={processing}
              onClick={() => setAcceptDialogOpen(true)}
              startIcon={processing ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ flex: { sm: 1 } }}
            >
              {t('userTutorBookings.changeProposal.acceptNewTime', 'Accept new time')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size={compact ? 'small' : 'large'}
              disabled={processing}
              onClick={() => {
                setDeclineReason('');
                setDeclineDialogOpen(true);
              }}
              sx={{ flex: { sm: 1 }, borderColor: 'error.main', color: 'error.main' }}
            >
              {t('userTutorBookings.changeProposal.keepOriginalTime', 'Keep original time')}
            </Button>
          </Stack>
        )}

        {proposal.status === TutorBookingChangeStatus.DECLINED && proposal.replyComment?.trim() && (
          <Box sx={{ mt: compact ? 0 : blockSpacing, mb: compact ? sectionGap : 0 }}>
            {compact ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t('userTutorBookings.changeProposal.yourReply', 'Your reply')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {proposal.replyComment.trim()}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" display="block">
                {t('userTutorBookings.changeProposal.yourReply', 'Your reply')}: {proposal.replyComment.trim()}
              </Typography>
            )}
          </Box>
        )}

        {proposal.repliedAt && terminal && compact && (
          <>
            <Typography variant="caption" color="text.secondary">
              {t('userTutorBookings.changeProposal.repliedAt', 'Responded')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatProposalDateTime(proposal.repliedAt, i18n.language, timezone)}
            </Typography>
          </>
        )}

        {proposal.repliedAt && terminal && !compact && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            {t('userTutorBookings.changeProposal.repliedAt', 'Responded')}:{' '}
            {formatProposalDateTime(proposal.repliedAt, i18n.language, timezone)}
          </Typography>
        )}
      </Card>

      {dialogs}
    </>
  );
}
